/**
 * Overview Tab Component
 * Compliance summary and priority actions
 */

import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Shield, AlertTriangle, CheckCircle, Clock, ChevronRight, FileText,
  ClipboardList, Stethoscope, Pill, GraduationCap
} from "lucide-react";

const OverviewTab = ({ hopeStatus, f2fEncounters, medReconciliation, teachBack, isDark, onNavigate }) => {
  // Calculate overall compliance score
  const totalItems = 
    (hopeStatus?.total_count || 0) + 
    (f2fEncounters?.total_count || 0) + 
    (medReconciliation?.total_count || 0) + 
    (teachBack?.total_count || 0);
  
  const compliantItems = 
    (hopeStatus?.complete_count || 0) + 
    (f2fEncounters?.complete_count || 0) + 
    (medReconciliation?.complete_count || 0) + 
    (teachBack?.verified_count || 0);
  
  const complianceScore = totalItems > 0 ? Math.round((compliantItems / totalItems) * 100) : 100;

  return (
    <div className="space-y-6">
      {/* Compliance Score */}
      <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <Shield className="w-4 h-4 text-emerald-400" />
            Overall Compliance Score
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <div className="relative w-24 h-24">
              <svg className="w-24 h-24 transform -rotate-90">
                <circle
                  cx="48" cy="48" r="40"
                  fill="none"
                  stroke={isDark ? "#334155" : "#e5e7eb"}
                  strokeWidth="8"
                />
                <circle
                  cx="48" cy="48" r="40"
                  fill="none"
                  stroke={complianceScore >= 90 ? "#22c55e" : complianceScore >= 70 ? "#eab308" : "#ef4444"}
                  strokeWidth="8"
                  strokeDasharray={`${complianceScore * 2.51} 251`}
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className={`text-2xl font-bold ${
                  complianceScore >= 90 ? "text-green-400" :
                  complianceScore >= 70 ? "text-yellow-400" : "text-red-400"
                }`}>
                  {complianceScore}%
                </span>
              </div>
            </div>
            <div className="flex-1">
              <div className="grid grid-cols-2 gap-2">
                <div className={`p-2 rounded ${isDark ? "bg-slate-800" : "bg-gray-50"}`}>
                  <p className="text-xs text-gray-500">HOPE</p>
                  <p className="font-medium">{hopeStatus?.complete_count || 0}/{hopeStatus?.total_count || 0}</p>
                </div>
                <div className={`p-2 rounded ${isDark ? "bg-slate-800" : "bg-gray-50"}`}>
                  <p className="text-xs text-gray-500">F2F</p>
                  <p className="font-medium">{f2fEncounters?.complete_count || 0}/{f2fEncounters?.total_count || 0}</p>
                </div>
                <div className={`p-2 rounded ${isDark ? "bg-slate-800" : "bg-gray-50"}`}>
                  <p className="text-xs text-gray-500">Med Rec</p>
                  <p className="font-medium">{medReconciliation?.complete_count || 0}/{medReconciliation?.total_count || 0}</p>
                </div>
                <div className={`p-2 rounded ${isDark ? "bg-slate-800" : "bg-gray-50"}`}>
                  <p className="text-xs text-gray-500">Teach-back</p>
                  <p className="font-medium">{teachBack?.verified_count || 0}/{teachBack?.total_count || 0}</p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Priority Actions */}
      <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-orange-400" />
            Priority Actions Required
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {(hopeStatus?.overdue_count > 0 || f2fEncounters?.critical_count > 0 || 
              medReconciliation?.incomplete_count > 0 || teachBack?.unverified_count > 0) ? (
              <>
                {hopeStatus?.overdue_count > 0 && (
                  <div 
                    className={`p-3 rounded flex items-center justify-between cursor-pointer hover:bg-opacity-80 ${isDark ? "bg-red-900/20 border border-red-500/30" : "bg-red-50"}`}
                    onClick={() => onNavigate("hope")}
                    data-testid="hope-priority-action"
                  >
                    <div className="flex items-center gap-3">
                      <ClipboardList className="w-5 h-5 text-red-400" />
                      <div>
                        <p className="font-medium text-red-400">{hopeStatus.overdue_count} Overdue HOPE Assessments</p>
                        <p className="text-xs text-gray-500">CMS requires timely HOPE completion</p>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-red-400" />
                  </div>
                )}
                
                {f2fEncounters?.critical_count > 0 && (
                  <div 
                    className={`p-3 rounded flex items-center justify-between cursor-pointer hover:bg-opacity-80 ${isDark ? "bg-red-900/20 border border-red-500/30" : "bg-red-50"}`}
                    onClick={() => onNavigate("f2f")}
                    data-testid="f2f-priority-action"
                  >
                    <div className="flex items-center gap-3">
                      <Stethoscope className="w-5 h-5 text-red-400" />
                      <div>
                        <p className="font-medium text-red-400">{f2fEncounters.critical_count} Critical F2F Encounters</p>
                        <p className="text-xs text-gray-500">Required for recertification compliance</p>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-red-400" />
                  </div>
                )}
                
                {medReconciliation?.incomplete_count > 0 && (
                  <div 
                    className={`p-3 rounded flex items-center justify-between cursor-pointer hover:bg-opacity-80 ${isDark ? "bg-yellow-900/20 border border-yellow-500/30" : "bg-yellow-50"}`}
                    onClick={() => onNavigate("medication")}
                    data-testid="medrec-priority-action"
                  >
                    <div className="flex items-center gap-3">
                      <Pill className="w-5 h-5 text-yellow-400" />
                      <div>
                        <p className="font-medium text-yellow-400">{medReconciliation.incomplete_count} Med Reconciliations Incomplete</p>
                        <p className="text-xs text-gray-500">Required for survey compliance</p>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-yellow-400" />
                  </div>
                )}
                
                {teachBack?.unverified_count > 0 && (
                  <div 
                    className={`p-3 rounded flex items-center justify-between cursor-pointer hover:bg-opacity-80 ${isDark ? "bg-yellow-900/20 border border-yellow-500/30" : "bg-yellow-50"}`}
                    onClick={() => onNavigate("teachback")}
                    data-testid="teachback-priority-action"
                  >
                    <div className="flex items-center gap-3">
                      <GraduationCap className="w-5 h-5 text-yellow-400" />
                      <div>
                        <p className="font-medium text-yellow-400">{teachBack.unverified_count} Teach-backs Unverified</p>
                        <p className="text-xs text-gray-500">Family education acknowledgment needed</p>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-yellow-400" />
                  </div>
                )}
              </>
            ) : (
              <div className={`p-6 text-center rounded ${isDark ? "bg-green-900/20 border border-green-500/30" : "bg-green-50"}`}>
                <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-400" />
                <p className="font-medium text-green-400">All Clear!</p>
                <p className="text-sm text-gray-500">No priority compliance actions required</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* CMS Requirements Checklist */}
      <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <FileText className="w-4 h-4 text-cyan-400" />
            CMS CoP Requirements Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3">
            {[
              { name: "HOPE Admission Assessment", status: hopeStatus?.admission_complete ? "complete" : "pending" },
              { name: "HOPE Updates (15 Days)", status: hopeStatus?.updates_current ? "complete" : "pending" },
              { name: "F2F Encounters", status: f2fEncounters?.all_compliant ? "complete" : "pending" },
              { name: "Physician Attestations", status: f2fEncounters?.attestations_complete ? "complete" : "pending" },
              { name: "Medication Reconciliation", status: medReconciliation?.all_complete ? "complete" : "pending" },
              { name: "Family Education Verified", status: teachBack?.all_verified ? "complete" : "pending" },
            ].map((item, i) => (
              <div 
                key={i} 
                className={`flex items-center justify-between p-2 rounded ${
                  item.status === "complete" 
                    ? isDark ? "bg-green-900/20 border border-green-500/30" : "bg-green-50"
                    : isDark ? "bg-yellow-900/20 border border-yellow-500/30" : "bg-yellow-50"
                }`}
              >
                <span className="text-sm">{item.name}</span>
                {item.status === "complete" ? (
                  <CheckCircle className="w-4 h-4 text-green-400" />
                ) : (
                  <Clock className="w-4 h-4 text-yellow-400" />
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OverviewTab;
